/**
 * Better Husband — KPI Analytics Service
 * ----------------------------------------
 * Phase 2+3: Structured event tracking with offline queue.
 *
 * Rules (from implementation plan):
 * - Each event gets a deterministic event_id = hash(user_id + event_type + timestamp + key_prop)
 * - Events are queued in localStorage when offline
 * - Flush in batches of ≤20 with automatic retry
 * - Only meaningful actions are tracked (no scroll, no micro-taps)
 */

import { logEvent as gaLogEvent, setUserId as gaSetUserId } from '@/lib/firebaseAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const QUEUE_KEY   = 'bh_event_queue';
const MAX_BATCH   = 20;
const APP_VERSION = '1.1.1';

// ─── Phase 12: Feature flag — set REACT_APP_KPI_ENABLED=false or
//              localStorage.setItem('kpi_enabled','false') to disable.
// Defaults ON for all users (100% rollout in v0.1.31).
const KPI_ENABLED =
  process.env.REACT_APP_KPI_ENABLED !== 'false' &&
  localStorage.getItem('kpi_enabled') !== 'false';

// ─── Deterministic event_id hash ───────────────────────────────────────────
// djb2-style: fast, no dependencies, browser-safe.
function _djb2(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash; // convert to 32-bit signed int
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

function generateEventId(userId, eventType, timestamp, extra = '') {
  const raw = `${userId}|${eventType}|${timestamp}|${extra}`;
  const h1  = _djb2(raw);
  const h2  = _djb2(raw + 'salt2');
  const h3  = _djb2(raw + 'salt3');
  const h4  = _djb2(raw + 'salt4');
  return `${h1}-${h2}-${h3}-${h4}`;
}

// ─── Device info ────────────────────────────────────────────────────────────
function _getDevice() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua))  return 'ios';
  if (/Android/.test(ua))           return 'android';
  return 'web';
}

// ─── LocalStorage queue ─────────────────────────────────────────────────────
function _loadQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

function _saveQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // storage full — drop oldest events
    const trimmed = queue.slice(-50);
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed)); } catch {}
  }
}

function _enqueue(event) {
  if (!KPI_ENABLED) return; // Phase 12 feature flag
  const queue = _loadQueue();
  // De-duplicate by event_id before enqueuing
  if (queue.some(e => e.event_id === event.event_id)) return;
  queue.push(event);
  _saveQueue(queue);
}

// ─── Flush ──────────────────────────────────────────────────────────────────
let _flushing = false;
// Jest renders real pages: without this, every test run posted onboarding
// events to the production backend (622 of them as "test-user-123").
const _SENDS = process.env.NODE_ENV !== 'test';

async function flush() {
  if (_flushing || !_SENDS) return;
  const queue = _loadQueue();
  if (!queue.length) return;

  _flushing = true;
  try {
    // Send in batches of MAX_BATCH
    const batch = queue.slice(0, MAX_BATCH);

    const authToken = await _getAuthToken();
    const headers   = { 'Content-Type': 'application/json' };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

    const res = await fetch(`${BACKEND_URL}/api/events/batch`, {
      method:  'POST',
      headers,
      body:    JSON.stringify({ events: batch }),
    });

    if (res.ok) {
      // Remove sent events from queue
      const sent     = new Set(batch.map(e => e.event_id));
      const remaining = _loadQueue().filter(e => !sent.has(e.event_id));
      _saveQueue(remaining);
    }
    // On failure we keep events in the queue for retry next time
  } catch {
    // Network error — leave queue intact for next flush
  } finally {
    _flushing = false;
  }
}

// Unload-safe flush: a normal fetch() can be aborted mid-flight when the tab
// closes (flush() also awaits an async auth-token lookup first, widening that
// window), silently stranding the event in the local queue forever if the
// user never comes back. sendBeacon is synchronous and browser-guaranteed to
// survive page unload — used here instead of fetch for exactly that reason.
// events/batch doesn't require auth (no Depends(get_current_user)), so
// skipping the Authorization header sendBeacon can't set anyway costs nothing.
function flushBeacon() {
  try {
    if (!_SENDS || typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    const queue = _loadQueue();
    if (!queue.length) return;
    const batch = queue.slice(0, MAX_BATCH);
    const blob = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' });
    navigator.sendBeacon(`${BACKEND_URL}/api/events/batch`, blob);
    // Not cleared from the queue: sendBeacon gives no delivery confirmation,
    // and events_raw upserts on event_id (ignore_duplicates), so a page that
    // survives (tab switch, not a real close) will just harmlessly re-send
    // the same batch on the next flush() rather than risk losing it.
  } catch {
    // best-effort only
  }
}

async function _getAuthToken() {
  try {
    const { supabase } = await import('@/lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch {
    return null;
  }
}

// ─── Internal track helper ──────────────────────────────────────────────────
function _track(eventType, userId, extraProps = {}, extraIdKey = '') {
  if (!userId) return; // never track without user context

  const timestamp = new Date().toISOString();
  const event_id  = generateEventId(userId, eventType, timestamp, extraIdKey);

  const event = {
    event_id,
    event_type:  eventType,
    user_id:     userId,
    timestamp,
    app_version: APP_VERSION,
    device:      _getDevice(),
    properties:  extraProps,
  };

  _enqueue(event);

  // Forward to Firebase Analytics / GA4 (fire-and-forget)
  gaLogEvent(eventType, { ...extraProps, app_version: APP_VERSION, device: _getDevice() });

  // Opportunistically flush immediately (best-effort)
  flush();
}

// ─── Auto-flush on visibility change ────────────────────────────────────────
if (typeof document !== 'undefined') {
  // 'hidden' fires on tab switch/backgrounding too, not just closing — but
  // there's no reliable way to tell those apart in advance, so always use the
  // unload-safe path here rather than the plain fetch() in flush().
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushBeacon();
  });
  // Belt-and-suspenders for browsers (notably Safari/iOS) where
  // visibilitychange doesn't reliably fire on an actual tab/app close.
  window.addEventListener('pagehide', flushBeacon);
  // Periodic flush every 30 seconds
  setInterval(flush, 30_000);
}

// ═══════════════════════════════════════════════════════════════════════════
// Public API — only meaningful events, no noise
// ═══════════════════════════════════════════════════════════════════════════

const ANON_ID_KEY        = 'bh_anon_tracking_id';
const FIRST_OPEN_KEY     = 'bh_first_open_sent';

/**
 * Get or create a stable anonymous device ID.
 * Persists in localStorage so it survives across sessions.
 * Once the user authenticates the real user_id takes over,
 * but the anon ID is preserved so we can link the two.
 */
export function getOrCreateAnonId() {
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id = 'anon_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch {
    return 'anon_fallback_' + Date.now().toString(36);
  }
}

/**
 * Fire a `first_open` event exactly once per device, at the very first
 * app launch — before any authentication or onboarding.
 * Uses the anonymous device ID as user_id so it is tracked even without
 * an account. A localStorage flag prevents re-firing on subsequent launches.
 *
 * @param {string} anonId   - the anonymous device ID (from getOrCreateAnonId)
 * @param {object} options  - optional { platform, app_version }
 */
export function trackFirstOpen(anonId, { platform, appVersion } = {}) {
  if (!anonId) return;
  try {
    if (localStorage.getItem(FIRST_OPEN_KEY)) return; // already fired
    localStorage.setItem(FIRST_OPEN_KEY, '1');
  } catch {
    // storage unavailable — fire anyway, idempotency handled by event_id
  }
  _track('first_open', anonId, { platform, app_version: appVersion, is_first_open: true }, 'first_open');
}

/**
 * Track app open.
 * Call once per session on initial load.
 * If the user is authenticated pass their real userId.
 * If not yet authenticated pass the anonymous device ID so pre-auth
 * sessions are still counted.
 */
export function trackAppOpen(userId, { phase, engineMode, isAnonymous, anonymous_id } = {}) {
  if (!userId) return;
  if (!isAnonymous) gaSetUserId(userId); // Set GA4 user identity for real users only
  _track('app_open', userId, {
    phase,
    engine_mode: engineMode,
    is_anonymous: isAnonymous || false,
    ...(anonymous_id ? { anonymous_id } : {}),
  }, '');
}

/**
 * Track when a suggestion is shown to the user.
 */
export function trackSuggestionShown(userId, { suggestionId, category, role, phase, engineMode } = {}) {
  _track('suggestion_shown', userId, {
    suggestion_id: suggestionId,
    category,
    role,
    phase,
    engine_mode: engineMode,
  }, suggestionId || '');
}

/**
 * Track gesture action: DONE | SKIP | INTERNALIZED
 */
export function trackSuggestionAction(userId, { suggestionId, action, phase, engineMode } = {}) {
  if (!['DONE', 'SKIP', 'INTERNALIZED'].includes(action)) return;
  _track('suggestion_action', userId, {
    suggestion_id: suggestionId,
    action,
    phase,
    engine_mode: engineMode,
  }, `${suggestionId}|${action}`);
}

/**
 * Track when paywall is shown.
 */
export function trackPaywallViewed(userId, { source } = {}) {
  _track('paywall_viewed', userId, { source }, source || '');
}

/**
 * Track when a trial starts.
 */
export function trackTrialStarted(userId, { planType, price } = {}) {
  _track('trial_started', userId, { plan_type: planType, price }, planType || '');
}

/**
 * Track when a trial converts to a paid subscription.
 */
export function trackTrialConverted(userId, { planType, price } = {}) {
  _track('trial_converted', userId, { plan_type: planType, price }, planType || '');
}

/**
 * Track subscription started (Stripe webhook confirms on server; call this on client too for funnel).
 */
export function trackSubscriptionStarted(userId, { planType, price } = {}) {
  _track('subscription_started', userId, { plan_type: planType, price }, planType || '');
}

/**
 * Track subscription cancelled.
 */
export function trackSubscriptionCancelled(userId, { planType, reason } = {}) {
  _track('subscription_cancelled', userId, { plan_type: planType, reason }, `${planType}|${reason || ''}`);
}

/**
 * Track onboarding step reached.
 * Fires once per step per user per day (deduplicated by event_id hash).
 */
export function trackOnboardingStep(userId, { step, stepName, ageCluster, stepData } = {}) {
  const normalizedStepData = stepData && typeof stepData === 'object' ? stepData : {};
  const normalizedAgeCluster = ageCluster || normalizedStepData.age_cluster;
  _track(
    'onboarding_step',
    userId,
    {
      step,
      step_name: stepName,
      age_cluster: normalizedAgeCluster,
      ...normalizedStepData,
    },
    `${step}|${normalizedAgeCluster || ''}`,
  );
}

// Export flush so callers can trigger it manually (e.g., on logout)
export { flush };
